'use client'

import { useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useCart } from '@/lib/CartContext'
import Image from 'next/image'

interface CartDrawerProps {
  isOpen: boolean
  onClose: () => void
}

export default function CartDrawer({ isOpen, onClose }: CartDrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null)
  const { items, updateItemQuantity, removeItem, itemCount, clearCart } = useCart()

  // Format price to always show 2 decimal places with validation
  const formatPrice = (price: string | number | undefined): string => {
    if (price === undefined || price === null) return '0.00'
    
    // Handle invalid input by defaulting to 0
    const numericPrice = typeof price === 'string' 
      ? (isNaN(parseFloat(price)) ? 0 : parseFloat(price)) 
      : (isNaN(price) ? 0 : price)
      
    return numericPrice.toFixed(2)
  }

  // Calculate total price with validation
  const total = items.reduce((sum, item) => {
    const itemPrice = typeof item.price === 'string' 
      ? (isNaN(parseFloat(item.price)) ? 0 : parseFloat(item.price))
      : (isNaN(Number(item.price)) ? 0 : Number(item.price))
    
    return sum + (itemPrice * item.quantity)
  }, 0)

  // Handle Shopify checkout
  const handleCheckout = async () => {
    if (itemCount === 0) return

    try {
      // Map cart items to Shopify's expected format
      const cartItems = items.map(item => {
        console.log('Cart item details:', {
          name: item.name,
          variantId: item.variantId,
          price: item.price,
          quantity: item.quantity
        });
        
        return {
          id: item.variantId,
          quantity: item.quantity
        };
      });

      console.log('Preparing checkout with items:', JSON.stringify(cartItems, null, 2));

      // Show loading state or feedback to user
      // You could add a state variable here: const [isLoading, setIsLoading] = useState(false)
      // setIsLoading(true)
      
      console.log('Sending cart items to Shopify API');

      const response = await fetch('/api/cart/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: cartItems }),
      });

      console.log('Checkout raw response:', response.status, response.statusText);
      
      const data = await response.json();
      
      console.log('Checkout API response:', JSON.stringify(data, null, 2));
      
      // Display more info if there's an error
      if (!response.ok || !data.success) {
        console.error('Checkout error details:', data);
        throw new Error(data.error || 'Failed to create cart');
      }

      if (!data.checkoutUrl) {
        throw new Error('No checkout URL returned');
      }

      // Log the checkout URL before redirect
      console.log('Redirecting to checkout URL:', data.checkoutUrl);
      
      // CRITICAL: Don't modify the checkout URL - use exactly what the server returned
      const checkoutUrl = data.checkoutUrl;
      
      console.log('Final checkout URL:', checkoutUrl);
      
      // Redirect to the checkout URL
      window.location.href = checkoutUrl;
      
    } catch (error) {
      console.error('Checkout error:', error);
      alert(`Failed to create checkout: ${error instanceof Error ? error.message : 'Unknown error'}`);
      // setIsLoading(false) - if you added loading state
    }
  }

  // Close drawer when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
          />
          
          {/* Drawer */}
          <motion.div
            ref={drawerRef}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-[#111] border-l border-[#333] z-50"
          >
            <div className="p-4 h-full flex flex-col">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl text-white font-mono">Shopping Cart ({itemCount})</h2>
                <button onClick={onClose} className="text-gray-400 hover:text-white">
                  ✕
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto">
                {items.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    Your cart is empty
                  </div>
                ) : (
                  <div className="space-y-4">
                    {items.map((item) => (
                      <div key={item.variantId} className="flex gap-4 bg-black/30 p-4 rounded-lg">
                        <div className="w-20 h-20 relative bg-black/50 rounded overflow-hidden">
                          <Image
                            src={item.image}
                            alt={item.name}
                            fill
                            className="object-cover"
                          />
                        </div>
                        
                        <div className="flex-1">
                          <h3 className="text-white font-mono text-sm">{item.name}</h3>
                          <div className="flex justify-between mt-1">
                            <p className="text-gray-400 text-xs">Unit: ${formatPrice(item.price)}</p>
                            <p className="text-[#33ff33] text-xs">Subtotal: ${formatPrice(parseFloat(formatPrice(item.price)) * item.quantity)}</p>
                          </div>
                          
                          <div className="flex items-center gap-2 mt-2">
                            <button
                              onClick={() => updateItemQuantity(item.variantId, Math.max(0, item.quantity - 1))}
                              className="w-6 h-6 flex items-center justify-center bg-[#222] hover:bg-[#333] text-white rounded"
                            >
                              -
                            </button>
                            <span className="text-white w-8 text-center">{item.quantity}</span>
                            <button
                              onClick={() => updateItemQuantity(item.variantId, item.quantity + 1)}
                              className="w-6 h-6 flex items-center justify-center bg-[#222] hover:bg-[#333] text-white rounded"
                            >
                              +
                            </button>
                            <button
                              onClick={() => removeItem(item.variantId)}
                              className="ml-auto text-red-500 hover:text-red-400 text-sm"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="border-t border-[#333] pt-4 mt-4 space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-gray-400 font-mono text-sm">
                    <span>Items ({itemCount})</span>
                    <span>${formatPrice(total)}</span>
                  </div>
                  <div className="flex justify-between text-white font-mono">
                    <span>Total</span>
                    <span className="text-[#33ff33] font-bold">${formatPrice(total)}</span>
                  </div>
                </div>
                
                <button 
                  onClick={handleCheckout}
                  disabled={itemCount === 0}
                  className="w-full bg-[#33ff33] text-black py-3 px-4 rounded font-mono hover:bg-[#33ff33]/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Proceed to Checkout
                </button>
                
                {itemCount > 0 && (
                  <button
                    onClick={clearCart}
                    className="w-full border border-red-500 text-red-500 py-2 px-4 rounded font-mono hover:bg-red-500/10"
                  >
                    Clear Cart
                  </button>
                )}

                {/* Payment Methods */}
                <div className="pt-4 space-y-3">
                  <div className="flex items-center justify-center gap-3">
                    <Image src="/visa.svg" alt="Visa" width={32} height={20} className="opacity-50 hover:opacity-100 transition-opacity" />
                    <Image src="/mastercard.svg" alt="Mastercard" width={32} height={20} className="opacity-50 hover:opacity-100 transition-opacity" />
                    <Image src="/amex.svg" alt="American Express" width={32} height={20} className="opacity-50 hover:opacity-100 transition-opacity" />
                    <Image src="/btc.svg" alt="Bitcoin" width={20} height={20} className="opacity-50 hover:opacity-100 transition-opacity" />
                  </div>
                  
                  {/* Anti-PayPal Message */}
                  <div className="text-[#666] text-xs text-center font-mono">
                    <span className="text-red-500">※</span> PayPal not accepted. We respect ourselves.
                  </div>
                </div>

                {/* Shipping Policy Link */}
                <div className="text-center">
                  <a 
                    href="/shipping-policy" 
                    target="_blank"
                    className="text-xs text-[#666] hover:text-[#33ff33] font-mono transition-colors"
                  >
                    Shipping & Handling Policy_
                  </a>
                </div>

                {/* Security Notice */}
                <div className="text-[#444] text-[10px] text-center font-mono mt-2">
                  SECURE PAYMENT PROTOCOL :: NODE_ID: {Math.random().toString(36).substring(2, 8).toUpperCase()}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
} 